import { useState } from "react";
import {
  activateGatePassTemplate,
  createGatePassTemplate,
  deleteGatePassTemplate,
  listGatePassTemplates,
} from "./api";
import { useAdminResource } from "./use-admin-resource";
import { useSubscription } from "./useSubscription";
import { LockedFeature } from "./LockedFeature";
import { useConfirm } from "../../components/useConfirm";
import { useToast } from "../../components/useToast";
import type { GatePassTemplate, GatePassVariant } from "./types";

const VARIANTS: { value: GatePassVariant; label: string }[] = [
  { value: "classic", label: "Classic" },
  { value: "corporate", label: "Corporate" },
  { value: "minimal", label: "Minimal" },
  { value: "vip", label: "VIP" },
];

type CreateForm = {
  name: string;
  variant: GatePassVariant;
  accentColor: string;
  footerText: string;
};

const INITIAL_FORM: CreateForm = {
  name: "",
  variant: "corporate",
  accentColor: "#ea580c",
  footerText: "",
};

function GatePassPreview({ template }: { template: GatePassTemplate }) {
  return (
    <div
      className={`gate-pass-preview gate-pass-preview--${template.variant}`}
      style={{ borderColor: template.accent_color }}
    >
      <p className="gate-pass-preview-brand" style={{ color: template.accent_color }}>
        0todeploy VMS
      </p>
      <p className="gate-pass-preview-name">Visitor Name</p>
      <p className="gate-pass-preview-meta">Host &middot; Company</p>
      {template.footer_text ? <p className="gate-pass-preview-footer">{template.footer_text}</p> : null}
    </div>
  );
}

export function GatePassTemplatesPanel() {
  const { data: templates, loading, error, refresh } = useAdminResource(
    () => listGatePassTemplates(),
    [],
  );
  const { data: subscription, loading: subscriptionLoading } = useSubscription();
  const { showSuccess, showError } = useToast();
  const confirm = useConfirm();

  const [form, setForm] = useState<CreateForm>(INITIAL_FORM);
  const [createPending, setCreatePending] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<number | null>(null);

  async function submitCreate() {
    if (!form.name.trim()) {
      setCreateError("Name is required");
      return;
    }
    setCreatePending(true);
    setCreateError(null);
    try {
      await createGatePassTemplate({
        name: form.name.trim(),
        variant: form.variant,
        accent_color: form.accentColor,
        footer_text: form.footerText.trim() || undefined,
      });
      showSuccess(`Created "${form.name.trim()}"`);
      setForm(INITIAL_FORM);
      await refresh();
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Failed to create template";
      setCreateError(message);
      showError(message);
    } finally {
      setCreatePending(false);
    }
  }

  async function activate(template: GatePassTemplate) {
    if (template.id == null) return;
    setPendingId(template.id);
    try {
      await activateGatePassTemplate(template.id);
      showSuccess(`"${template.name}" is now the active gate pass`);
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to activate template");
    } finally {
      setPendingId(null);
    }
  }

  async function remove(template: GatePassTemplate) {
    if (template.id == null) return;
    const ok = await confirm({
      title: "Delete gate pass template?",
      message: `"${template.name}" will be removed${
        template.is_active ? " -- the oldest remaining template will become active" : ""
      }.`,
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;

    setPendingId(template.id);
    try {
      await deleteGatePassTemplate(template.id);
      showSuccess(`Deleted "${template.name}"`);
      await refresh();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to delete template");
    } finally {
      setPendingId(null);
    }
  }

  const rows = templates ?? [];

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Gate Pass Templates</h2>
        <span>{rows.length} templates</span>
      </header>
      <p className="management-note">
        Design multiple visitor gate pass looks and switch which one is active. The active
        template is used for the badge shown right after check-in.
      </p>

      <LockedFeature
        feature="template_customization"
        featureLabel="Custom gate pass templates"
        subscription={subscription}
        loading={subscriptionLoading}
      >
        <form
          className="action-form action-card"
          onSubmit={(event) => {
            event.preventDefault();
            void submitCreate();
          }}
        >
          <label htmlFor="gpt-name">Create Gate Pass Template</label>
          <div className="checkin-fields">
            <input
              id="gpt-name"
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              placeholder="Name *"
            />
            <select
              className="search-input"
              value={form.variant}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, variant: event.target.value as GatePassVariant }))
              }
            >
              {VARIANTS.map((variant) => (
                <option key={variant.value} value={variant.value}>
                  {variant.label}
                </option>
              ))}
            </select>
            <input
              type="color"
              className="color-input"
              value={form.accentColor}
              onChange={(event) => setForm((prev) => ({ ...prev, accentColor: event.target.value }))}
            />
            <input
              value={form.footerText}
              onChange={(event) => setForm((prev) => ({ ...prev, footerText: event.target.value }))}
              placeholder="Footer text (optional)"
            />
          </div>
          <button className="refresh-btn action-submit" type="submit" disabled={createPending}>
            {createPending ? "Creating..." : "Create Template"}
          </button>
        </form>
      </LockedFeature>
      {createError ? <p className="error-banner action-error">{createError}</p> : null}

      {error ? <p className="error-banner">{error}</p> : null}

      <div className="gate-pass-template-grid">
        {rows.map((template) => (
          <article key={template.id ?? "builtin"} className="gate-pass-template-card">
            <GatePassPreview template={template} />
            <div className="gate-pass-template-info">
              <p className="gate-pass-template-name">
                {template.name}
                {template.is_active ? <span className="status-pill status-approved">Active</span> : null}
                {template.is_builtin ? <span className="status-pill">Built-in default</span> : null}
              </p>
              {!template.is_builtin ? (
                <div className="action-row">
                  {!template.is_active ? (
                    <button
                      className="refresh-btn"
                      type="button"
                      disabled={pendingId === template.id}
                      onClick={() => void activate(template)}
                    >
                      Activate
                    </button>
                  ) : null}
                  <button
                    className="refresh-btn danger-action-btn"
                    type="button"
                    disabled={pendingId === template.id}
                    onClick={() => void remove(template)}
                  >
                    Delete
                  </button>
                </div>
              ) : null}
            </div>
          </article>
        ))}
      </div>

      <button className="refresh-btn" type="button" onClick={() => void refresh()} disabled={loading}>
        {loading ? "Loading..." : "Refresh"}
      </button>
    </article>
  );
}
